import * as React from "react";
import { googleRegion } from "../validation/laenderprofil";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { useFormPrimitiveStyles } from "../formPrimitives";
import { useGooglePlaces } from "../google/useGooglePlaces";
import { SperrWert } from "../SperrWert";

const useLocal = makeStyles({
  wrap: { position: "relative" },
  list: {
    position: "absolute",
    zIndex: 40,
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    maxHeight: "264px",
    overflowY: "auto",
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    boxShadow: REFINED.cardShadow,
    paddingTop: "4px",
    paddingBottom: "4px",
  },
  opt: {
    ...shorthands.padding("9px", "12px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    color: REFINED.ink,
    cursor: "pointer",
  },
  optActive: { backgroundColor: REFINED.aub50 },
});

export interface CityAutocompleteProps {
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  land?: string;
  required?: boolean;
  placeholder?: string;
  fieldClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
}

export function CityAutocomplete({
  label,
  name,
  value,
  onChange,
  land,
  required,
  placeholder = "z. B. Aachen, Köln oder Wien",
  fieldClassName,
  labelClassName,
  inputClassName,
}: CityAutocompleteProps) {
  const s = useFormPrimitiveStyles();
  const l = useLocal();
  const { places } = useGooglePlaces();
  const [preds, setPreds] = React.useState<any[]>([]);
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const tokenRef = React.useRef<any>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const regionCode = googleRegion(land || "Deutschland");
  // Deutschland + direkte Nachbarländer
  const DACH_AND_NEIGHBORS = ["de", "at", "ch", "nl", "be", "lu", "fr", "pl", "cz", "dk"];
  const includedRegions = regionCode === "de" ? DACH_AND_NEIGHBORS : [regionCode];

  const fetchPreds = (q: string) => {
    if (!places || !q.trim()) {
      setPreds([]);
      setOpen(false);
      return;
    }
    if (!tokenRef.current) tokenRef.current = new places.AutocompleteSessionToken();

    places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: q,
      includedRegionCodes: includedRegions,
      includedPrimaryTypes: ["locality", "sublocality", "administrative_area_level_3"],
      language: "de",
      region: regionCode,
      sessionToken: tokenRef.current,
    })
      .then((res: any) => {
        setPreds(res?.suggestions ?? []);
        setOpen(true);
        setActive(0);
      })
      .catch((err: any) => {
        console.error("[GMaps City] fetchAutocompleteSuggestions Fehler:", err);
        setPreds([]);
      });
  };

  const onType = (v: string) => {
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPreds(v), 220);
  };

  const choose = async (sug: any) => {
    setOpen(false);
    setPreds([]);
    const pp = sug?.placePrediction;
    if (!pp) return;
    try {
      const place = pp.toPlace();
      await place.fetchFields({ fields: ["addressComponents"] });
      const comps: any[] = place.addressComponents ?? [];
      const get = (type: string) => comps.find((c) => (c.types || []).includes(type));
      const city = get("locality")?.longText ?? get("postal_town")?.longText ?? "";
      const area =
        get("sublocality")?.longText ??
        get("sublocality_level_1")?.longText ??
        get("neighborhood")?.longText ??
        "";

      let finalCity = city;
      if (city && area && area.toLowerCase() !== city.toLowerCase()) {
        finalCity = `${city}, ${area}`;
      } else if (!finalCity) {
        finalCity = pp.mainText?.text ?? pp.text?.text ?? "";
      }
      onChange(finalCity);
    } catch {
      // Fallback auf den Haupttext des Vorschlags
      const fallback = pp.mainText?.text ?? pp.text?.text ?? "";
      if (fallback) onChange(fallback);
    }
    tokenRef.current = null;
  };

  return (
    <div className={mergeClasses(s.field, fieldClassName)}>
      <label className={mergeClasses(s.label, labelClassName)} htmlFor={name}>
        {label}
        {required && <span className={s.req}> *</span>}
      </label>
      <div className={l.wrap}>
        <input
          id={name}
          name={name}
          type="text"
          className={mergeClasses(s.input, inputClassName)}
          value={value}
          onChange={(e) => onType(e.target.value)}
          onFocus={() => {
            if (preds.length > 0) setOpen(true);
          }}
          onBlur={() => {
            // Verzögert schließen, damit Klicks auf Optionen feuern können
            setTimeout(() => setOpen(false), 180);
          }}
          onKeyDown={(e) => {
            if (!open || preds.length === 0) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((prev) => (prev + 1) % preds.length);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((prev) => (prev - 1 + preds.length) % preds.length);
            } else if (e.key === "Enter") {
              e.preventDefault();
              choose(preds[active]);
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
        />
        <SperrWert />
        {open && preds.length > 0 && (
          <div className={l.list}>
            {preds.map((sug, i) => {
              const text =
                sug?.placePrediction?.text?.text ??
                sug?.placePrediction?.mainText?.text ??
                "";
              return (
                <div
                  key={i}
                  className={mergeClasses(l.opt, i === active && l.optActive)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(sug);
                  }}
                  onMouseEnter={() => setActive(i)}
                >
                  {text}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
